import React, { useEffect, useMemo, useState } from "react";
import { api, formatDate, formatTRY, todayISODate } from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { TableWrap, Thead, EmptyRow } from "@/components/DataTable";

const CATEGORIES = ["Parça", "Boyahane Malzeme", "Kaporta Malzeme"];

const normalizeCategory = (c) => {
  const v = (c || "").trim().toLowerCase();
  if (v === "parça") return "Parça";
  if (v === "kaporta malzeme") return "Kaporta Malzeme";
  // legacy "Nalbur" and anything else defaults to boyahane
  return "Boyahane Malzeme";
};

const emptyForm = {
  item_name: "",
  supplier: "",
  quantity: 1,
  unit_price: 0,
  total_cost: 0,
  paid_amount: 0,
  fully_paid: true,
  category: "Boyahane Malzeme",
  date: todayISODate(),
  note: "",
};

const tabToCategory = (tab) => {
  if (tab === "parca") return "Parça";
  if (tab === "kaporta") return "Kaporta Malzeme";
  if (tab === "boyahane") return "Boyahane Malzeme";
  return "Boyahane Malzeme";
};

const catToTab = (cat) => {
  const c = normalizeCategory(cat);
  if (c === "Parça") return "parca";
  if (c === "Kaporta Malzeme") return "kaporta";
  return "boyahane";
};

const CAT_STYLE = {
  Parça: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Boyahane Malzeme": "bg-amber-50 text-amber-800 border-amber-200",
  "Kaporta Malzeme": "bg-blue-50 text-blue-800 border-blue-200",
};

export default function Supplies() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all"); // all | parca | boyahane | kaporta
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    const res = await api.get("/supplies");
    setItems(res.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      category: tab === "all" ? "Boyahane Malzeme" : tabToCategory(tab),
    });
    setOpen(true);
  };
  const openEdit = (s) => {
    const total = Number(s.total_cost || 0);
    const paid = s.paid_amount == null ? total : Number(s.paid_amount);
    setEditing(s);
    setForm({
      item_name: s.item_name,
      supplier: s.supplier || "",
      quantity: s.quantity,
      unit_price: s.unit_price,
      total_cost: total,
      paid_amount: paid,
      fully_paid: paid >= total - 0.01,
      category: normalizeCategory(s.category),
      date: (s.date || todayISODate()).slice(0, 10),
      note: s.note || "",
    });
    setOpen(true);
  };

  const setField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "quantity" || field === "unit_price") {
        const qty = Number(field === "quantity" ? value : prev.quantity) || 0;
        const up = Number(field === "unit_price" ? value : prev.unit_price) || 0;
        next.total_cost = +(qty * up).toFixed(2);
        if (prev.fully_paid) next.paid_amount = next.total_cost;
      }
      if (field === "total_cost" && prev.fully_paid) {
        next.paid_amount = Number(value) || 0;
      }
      if (field === "fully_paid" && value) {
        next.paid_amount = Number(prev.total_cost) || 0;
      }
      return next;
    });
  };

  const save = async () => {
    if (!form.item_name.trim()) {
      toast.error("Malzeme adı gerekli");
      return;
    }
    const total = Number(form.total_cost) || 0;
    const paid = form.fully_paid ? total : Math.max(0, Math.min(Number(form.paid_amount) || 0, total));
    const payload = {
      item_name: form.item_name,
      supplier: form.supplier,
      quantity: Number(form.quantity) || 0,
      unit_price: Number(form.unit_price) || 0,
      total_cost: total,
      paid_amount: paid,
      category: CATEGORIES.includes(form.category) ? form.category : "Boyahane Malzeme",
      date: form.date,
      note: form.note,
    };
    if (editing) {
      await api.put(`/supplies/${editing.id}`, payload);
      toast.success("Güncellendi");
    } else {
      await api.post("/supplies", payload);
      toast.success("Eklendi");
    }
    setOpen(false);
    load();
  };

  const del = async () => {
    await api.delete(`/supplies/${deleteId}`);
    toast.success("Silindi");
    setDeleteId(null);
    load();
  };

  const filtered = items.filter((s) => {
    const t = q.toLowerCase();
    const itemTab = catToTab(s.category);
    if (tab !== "all" && itemTab !== tab) return false;
    return (
      !q ||
      s.item_name?.toLowerCase().includes(t) ||
      s.supplier?.toLowerCase().includes(t) ||
      s.category?.toLowerCase().includes(t)
    );
  });

  const monthStats = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const inMonth = items.filter((s) => {
      const d = new Date(s.date);
      return d.getFullYear() === y && d.getMonth() === m;
    });
    const sumBy = (fn) => inMonth.filter(fn).reduce((sum, s) => sum + (s.total_cost || 0), 0);
    // Toplam borç (tüm zaman, ödenmeyen kısım)
    const totalDebt = items.reduce((sum, s) => {
      const total = Number(s.total_cost) || 0;
      const paid = s.paid_amount == null ? total : Number(s.paid_amount) || 0;
      return sum + Math.max(total - paid, 0);
    }, 0);
    return {
      total: inMonth.reduce((sum, s) => sum + (s.total_cost || 0), 0),
      parca: sumBy((s) => catToTab(s.category) === "parca"),
      boyahane: sumBy((s) => catToTab(s.category) === "boyahane"),
      kaporta: sumBy((s) => catToTab(s.category) === "kaporta"),
      totalDebt,
    };
  }, [items]);

  return (
    <div data-testid="supplies-page" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Bu Ay · Toplam</p>
          <p className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-2 font-mono-num" data-testid="month-supply-total">
            {formatTRY(monthStats.total)}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Bu Ay · Parça</p>
          <p className="font-display text-2xl sm:text-3xl font-bold text-emerald-700 mt-2 font-mono-num" data-testid="month-parca-total">
            {formatTRY(monthStats.parca)}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold">Bu Ay · Boyahane</p>
          <p className="font-display text-2xl sm:text-3xl font-bold text-amber-700 mt-2 font-mono-num" data-testid="month-boyahane-total">
            {formatTRY(monthStats.boyahane)}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-blue-700 font-semibold">Bu Ay · Kaporta</p>
          <p className="font-display text-2xl sm:text-3xl font-bold text-blue-700 mt-2 font-mono-num" data-testid="month-kaporta-total">
            {formatTRY(monthStats.kaporta)}
          </p>
        </div>
      </div>

      {monthStats.totalDebt > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-center justify-between" data-testid="supplier-debt-alert">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-rose-700 font-semibold">Tedarikçilere Toplam Borç</p>
            <p className="text-xs text-rose-800 mt-0.5">Kısmi ödenen veya ödenmemiş malzemelerin kalan tutarı</p>
          </div>
          <p className="font-display text-2xl font-bold text-rose-700 font-mono-num">
            {formatTRY(monthStats.totalDebt)}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="supplies-tabs" className="bg-slate-100">
              <TabsTrigger data-testid="supplies-tab-all" value="all">Hepsi</TabsTrigger>
              <TabsTrigger data-testid="supplies-tab-parca" value="parca">Parça</TabsTrigger>
              <TabsTrigger data-testid="supplies-tab-boyahane" value="boyahane">Boyahane Malzeme</TabsTrigger>
              <TabsTrigger data-testid="supplies-tab-kaporta" value="kaporta">Kaporta Malzeme</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative w-full max-w-xs">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="supplies-search"
              placeholder="Malzeme, tedarikçi…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-white"
            />
          </div>
        </div>
        <Button data-testid="add-supply-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" /> Yeni Gider
        </Button>
      </div>

      <TableWrap>
        <Thead columns={["Tarih", "Malzeme", "Tedarikçi", "Kategori", "Adet", "Birim", "Toplam", ""]} />
        <tbody>
          {filtered.length === 0 && (
            <EmptyRow colSpan={8} testId="supplies-empty" message="Henüz gider kaydı yok." />
          )}
          {filtered.map((s) => {
            const cat = normalizeCategory(s.category);
            return (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`supply-row-${s.id}`}>
                <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{formatDate(s.date)}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{s.item_name}</div>
                  {s.note && <div className="text-xs text-slate-500">{s.note}</div>}
                </td>
                <td className="px-4 py-3 text-sm text-slate-700">{s.supplier || "-"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${CAT_STYLE[cat]}`}>
                    {cat}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-mono-num text-slate-700">{s.quantity}</td>
                <td className="px-4 py-3 text-sm font-mono-num text-slate-700">{formatTRY(s.unit_price)}</td>
                <td className="px-4 py-3 text-sm font-mono-num font-semibold text-slate-900">
                  {formatTRY(s.total_cost)}
                  {(() => {
                    const paid = s.paid_amount == null ? s.total_cost : Number(s.paid_amount);
                    const remaining = Math.max((Number(s.total_cost) || 0) - paid, 0);
                    if (remaining > 0.009) {
                      return (
                        <div className="text-[10px] font-normal text-rose-700 mt-0.5">
                          Borç: {formatTRY(remaining)}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button data-testid={`edit-supply-${s.id}`} variant="ghost" size="icon" onClick={() => openEdit(s)} className="h-8 w-8">
                      <Pencil className="w-4 h-4 text-slate-600" />
                    </Button>
                    <Button data-testid={`delete-supply-${s.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(s.id)} className="h-8 w-8 hover:bg-rose-50">
                      <Trash2 className="w-4 h-4 text-rose-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </TableWrap>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supply-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Gideri Düzenle" : "Yeni Malzeme Gideri"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Kategori</Label>
              <Tabs value={form.category} onValueChange={(v) => setField("category", v)} className="mt-1">
                <TabsList data-testid="supply-cat-tabs" className="bg-slate-100 grid grid-cols-3 w-full h-auto">
                  <TabsTrigger data-testid="supply-cat-parca" value="Parça" className="text-xs sm:text-sm">Parça</TabsTrigger>
                  <TabsTrigger data-testid="supply-cat-boyahane" value="Boyahane Malzeme" className="text-xs sm:text-sm">Boyahane Malzeme</TabsTrigger>
                  <TabsTrigger data-testid="supply-cat-kaporta" value="Kaporta Malzeme" className="text-xs sm:text-sm">Kaporta Malzeme</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div>
              <Label>Malzeme Adı</Label>
              <Input
                data-testid="supply-name-input"
                value={form.item_name}
                onChange={(e) => setField("item_name", e.target.value)}
                placeholder={
                  form.category === "Parça"
                    ? "Örn: Ön tampon, far, çamurluk…"
                    : form.category === "Kaporta Malzeme"
                    ? "Örn: Elektrot, kaynak teli, sac…"
                    : "Örn: Boya, tiner, macun, vernik…"
                }
              />
            </div>
            <div>
              <Label>Tedarikçi</Label>
              <Input
                data-testid="supply-supplier-input"
                value={form.supplier}
                onChange={(e) => setField("supplier", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Adet</Label>
                <Input
                  data-testid="supply-qty-input"
                  type="number"
                  step="0.01"
                  value={form.quantity}
                  onChange={(e) => setField("quantity", e.target.value)}
                  className="font-mono-num"
                />
              </div>
              <div>
                <Label>Birim Fiyat</Label>
                <Input
                  data-testid="supply-unit-input"
                  type="number"
                  step="0.01"
                  value={form.unit_price}
                  onChange={(e) => setField("unit_price", e.target.value)}
                  className="font-mono-num"
                />
              </div>
              <div>
                <Label>Toplam (₺)</Label>
                <Input
                  data-testid="supply-total-input"
                  type="number"
                  step="0.01"
                  value={form.total_cost}
                  onChange={(e) => setField("total_cost", e.target.value)}
                  className="font-mono-num font-semibold"
                />
              </div>
            </div>

            {/* Ödeme Durumu */}
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <input
                  data-testid="supply-fully-paid-checkbox"
                  type="checkbox"
                  id="supply-fully-paid"
                  checked={form.fully_paid}
                  onChange={(e) => setField("fully_paid", e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                />
                <Label htmlFor="supply-fully-paid" className="text-sm cursor-pointer">
                  Tamamı ödendi
                </Label>
              </div>
              {!form.fully_paid && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                      Ödenen Tutar (₺)
                    </Label>
                    <Input
                      data-testid="supply-paid-input"
                      type="number"
                      step="0.01"
                      value={form.paid_amount}
                      onChange={(e) => setField("paid_amount", e.target.value)}
                      className="font-mono-num h-9 text-sm bg-white"
                      placeholder="₺"
                    />
                  </div>
                  <div className="flex flex-col justify-end">
                    <p className="text-[11px] text-slate-500">Kalan Borç</p>
                    <p className="font-mono-num font-bold text-rose-700 text-base">
                      ₺{Math.max((Number(form.total_cost) || 0) - (Number(form.paid_amount) || 0), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label>Tarih</Label>
              <Input
                data-testid="supply-date-input"
                type="date"
                value={form.date?.slice(0, 10)}
                onChange={(e) => setField("date", e.target.value)}
              />
            </div>
            <div>
              <Label>Not</Label>
              <Input
                data-testid="supply-note-input"
                value={form.note}
                onChange={(e) => setField("note", e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-supply-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gider silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-supply" onClick={del} className="bg-rose-600 hover:bg-rose-700">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
